import {
	InteractionBarMainInfo,
	InteractionActions,
	WrapUp,
} from "@/components/molecules/interactionWorkspace";
import { useComponentStyle } from "./style";
import { InteractionControllsProps as InteractionBarProps } from "./types";
import { InteractionType } from "@/common/types";
import { CallInteractionActions } from "../interactionActions/CallInteractionActions";
import { WrapUpState, useWrapUpStore } from "@/common/stores/nonpersistingStores/wrapup";
import { useMemo, useState } from "react";
import { AppCacheState, CacheKey, useAppCacheStore } from "@/common/stores/persistingStores/app";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import {
	MessageBar,
	MessageBarBody,
	MessageBarGroup,
	Button,
	mergeClasses,
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { useVariableTextVisibility } from "@/hooks/variableText";
import { useInteractionBarCommonStyle } from "@/common/style/interactionBarCommonStyle";
import { AgentInteractionStatus } from "@/common/constants/callSession";
import { AiIndicator } from "@/components/atoms/aiIndicator";
import { Flex } from "@/components/atoms/flex";

export const InteractionBar = (props: InteractionBarProps) => {
	const {
		type,
		isVariableTextOpen: externalIsOpen,
		setIsVariableTextOpen: externalSetIsOpen,
	} = props;
	const componentStyle = useComponentStyle();
	const interactionWorkspaceCommonStyle = useInteractionBarCommonStyle();
	const [internalIsOpen, setInternalIsOpen] = useState<boolean>(true);

	const isVariableTextOpen =
		externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
	const setIsVariableTextOpen =
		externalSetIsOpen !== undefined ? externalSetIsOpen : setInternalIsOpen;

	const wrapUp = useWrapUpStore((state: WrapUpState) => state.wrapUp);
	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) =>
			state.preview ? state.preview : state.active
	);

	const currentInteractionIsPreview = useCurrentInteractionStore(
		(state: CurrentInteractionState) => Boolean(state.preview)
	);

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const isVariableTextVisible = useVariableTextVisibility();

	const actionComponent = useMemo(() => {
		if (
			wrapUp &&
			wrapUp.mediaId === currentInteraction?.id &&
			!currentInteractionIsPreview
		) {
			return <WrapUp />;
		}

		const isCall = currentInteraction?.type === InteractionType.Call;
		const isTerminated =
			callSession?.state === AgentInteractionStatus.Terminated;

		if (isCall) {
			if (!isTerminated) {
				return <CallInteractionActions />;
			} else {
				return <></>;
			}
		}

		return (
			<InteractionActions
				type={currentInteraction?.type ?? InteractionType.Generic}
				queueName={currentInteraction?.queueName}
			/>
		);
	}, [wrapUp, currentInteraction, callSession?.state]);

	const activeVariableValue = useMemo(() => {
		const callActiveVariableValue =
			callSession?.activeVariableText ??
			appStoreCache[CacheKey.SessionVariableText]?.toString() ??
			"";

		const messagingVariableTextArray =
			appStoreCache[CacheKey.MessagingVariableText];

		const cacheValue = messagingVariableTextArray
			? messagingVariableTextArray.find(
					(x: any) => x.key === currentInteraction?.id
				)
			: undefined;

		const currentInteractionActiveValue =
			currentInteraction?.activeVariableText !== ""
				? currentInteraction?.activeVariableText
				: undefined;

		const messagingActiveVariableValue =
			currentInteractionActiveValue ?? cacheValue?.value ?? "";

		return currentInteraction?.type === InteractionType.Call
			? callActiveVariableValue
			: messagingActiveVariableValue;
	}, [
		callSession,
		currentInteraction,
		appStoreCache[CacheKey.SessionVariableText],
	]);

	const isInWrapUp =
		wrapUp && wrapUp.mediaId === currentInteraction?.id && !currentInteractionIsPreview;

	const callOrMessagingAiGenerated =
		currentInteraction?.type === InteractionType.Call
			? !!callSession?.activeVariableTextIsAiGenerated
			: !!currentInteraction?.activeVariableTextIsAiGenerated;

	const isActiveVariableAiGenerated = isInWrapUp
		? !!wrapUp?.activeVariableTextIsAiGenerated
		: callOrMessagingAiGenerated;

	const activeVariableContainer = useMemo(() => {
		return (
			<Flex column>
				<div
					className={mergeClasses(
						componentStyle.contentToolTip,
						componentStyle.contentTextColor,
						componentStyle.activeVariableText
					)}
					dangerouslySetInnerHTML={{
						__html: activeVariableValue,
					}}
				/>
				{isActiveVariableAiGenerated && <AiIndicator mode="icon-with-text" className={mergeClasses(componentStyle.contentTextColor, componentStyle.aiIndicatorOffset)} />}
			</Flex>
		);
	}, [
		callSession,
		appStoreCache[CacheKey.SessionVariableText],
		activeVariableValue,
		currentInteraction,
		isActiveVariableAiGenerated,
	]);

	return (
		<div>
			<div className={interactionWorkspaceCommonStyle.interactionBar}>
				<InteractionBarMainInfo
					type={type}
					isVariableTextOpen={isVariableTextOpen}
					setIsVariableTextOpen={setIsVariableTextOpen}
				/>
				{actionComponent}
			</div>
			{isVariableTextVisible && isVariableTextOpen && (
				<MessageBarGroup className={componentStyle.messageBarGroup}>
					<MessageBar
						className={componentStyle.messageBar}
						icon={null}
						layout={type === InteractionType.Call ? "multiline" : undefined}
					>
						<MessageBarBody
							className={mergeClasses(
								componentStyle.messageBarBody,
								type === InteractionType.Call && componentStyle.messageBarTall
							)}
						>
							{activeVariableContainer}
						</MessageBarBody>
					</MessageBar>
					<Button
						appearance="transparent"
						className={componentStyle.dismissButton}
						icon={<DismissRegular />}
						onClick={() => setIsVariableTextOpen(false)}
					/>
				</MessageBarGroup>
			)}
		</div>
	);
};
